import { useEffect, useState, type DependencyList } from 'react';
import { AppState } from 'react-native';
import { errorMessage } from '../utils/errorMessage';

/**
 * Loads data whenever the dependencies change or the app comes back into view, keeping what is
 * already on screen while a refresh is under way.
 */
export function useRefreshingLoad<T>(load: () => Promise<T>, deps: DependencyList) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState('');
  const [returns, setReturns] = useState(0);
  useEffect(() => {
    const subscription = AppState.addEventListener('change', (state) => {
      if (state === 'active') setReturns((value) => value + 1);
    });
    return () => subscription.remove();
  }, []);
  useEffect(() => {
    let current = true;
    load()
      .then((value) => {
        if (!current) return;
        setData(value);
        setError('');
      })
      .catch((failure) => {
        if (current) setError(errorMessage(failure, 'Couldn’t load this. Please try again.'));
      });
    return () => {
      current = false;
    };
    // The caller's dependencies decide when to load again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, returns]);
  return { data, error };
}
